import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import DateRangeFilter, { filterByDateRange } from '../../components/DateRangeFilter';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;

const CashbookReport = () => {
  const { isAdmin } = useAuth();
  const [loans, setLoans] = useState([]);
  const [expenditures, setExpenditures] = useState([]);
  const [funds, setFunds] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [range, setRange] = useState({ from: null, to: null, label: 'All Time', presetKey: 'all' });

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const branchParam = selectedBranch !== 'all' ? { branch_id: selectedBranch } : {};
      const [expResp, fundResp, loanResp, branchResp, ctResp] = await Promise.all([
        api.get('/expenditures', { params: branchParam }),
        api.get('/fund-receipts', { params: branchParam }),
        api.get('/loans', { params: branchParam }),
        api.get('/branches'),
        api.get('/cash-transfers', { params: branchParam }).catch(() => ({ data: [] })),
      ]);
      setExpenditures(expResp.data);
      setFunds(fundResp.data);
      setLoans(loanResp.data);
      setBranches(branchResp.data);
      setTransfers(ctResp.data || []);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, [selectedBranch]);

  // Apply date-range filter on each list (each uses different date fields)
  const filteredFunds = useMemo(() => filterByDateRange(funds, range.from, range.to, 'date'), [funds, range]);
  const filteredExpenditures = useMemo(() => filterByDateRange(expenditures, range.from, range.to, 'date'), [expenditures, range]);
  const filteredLoans = useMemo(() => filterByDateRange(loans, range.from, range.to, 'disbursement_date'), [loans, range]);
  const filteredTransfers = useMemo(() => filterByDateRange(transfers, range.from, range.to, 'transfer_date'), [transfers, range]);
  const filteredB2C = useMemo(() => filteredTransfers.filter(t => t.direction === 'bank_to_cash'), [filteredTransfers]);
  const filteredC2B = useMemo(() => filteredTransfers.filter(t => t.direction === 'cash_to_bank'), [filteredTransfers]);

  const getBranchName = () => selectedBranch === 'all' ? 'All Branches' : branches.find(b => b.id === selectedBranch)?.name || 'Unknown';

  const totals = useMemo(() => {
    const totalReceipts = filteredFunds.reduce((s, f) => s + (f.amount || 0), 0);
    const totalExpenses = filteredExpenditures.reduce((s, e) => s + (e.amount || 0), 0);
    const totalLoansDisbursed = filteredLoans.reduce((s, l) => s + (l.loan_amount || 0), 0);
    const totalB2C = filteredB2C.reduce((s, t) => s + (t.amount || 0), 0);
    const totalC2B = filteredC2B.reduce((s, t) => s + (t.amount || 0), 0);
    return {
      totalReceipts, totalExpenses, totalLoansDisbursed, totalB2C, totalC2B,
      balance: (totalReceipts + totalB2C) - (totalExpenses + totalC2B + totalLoansDisbursed),
    };
  }, [filteredFunds, filteredExpenditures, filteredLoans, filteredB2C, filteredC2B]);

  const handlePrint = () => {
    const fundRows = filteredFunds.map((f, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${new Date(f.date).toLocaleDateString('en-IN')}</td><td>${f.fund_head}</td><td>${f.description||'-'}</td><td>${f.receipt_number || '-'}</td><td style="text-align:right">${fmt(f.amount)}</td></tr>`).join('');
    const expRows = filteredExpenditures.map((e, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${new Date(e.date).toLocaleDateString('en-IN')}</td><td>${e.category}</td><td>${e.description||'-'}</td><td>${e.voucher_number || '-'}</td><td style="text-align:right">${fmt(e.amount)}</td></tr>`).join('');
    const loanRows = filteredLoans.map((l, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${l.disbursement_date ? new Date(l.disbursement_date).toLocaleDateString('en-IN') : '-'}</td><td>${l.loan_id || '-'}</td><td>${l.entrepreneur_name || '-'}</td><td>${l.loan_type || '-'}</td><td style="text-align:right">${fmt(l.loan_amount)}</td></tr>`).join('');

    const body = `
      <p style="font-size:11px;color:#444;margin-bottom:8px"><b>Period:</b> ${range.label} &nbsp;|&nbsp; <b>Branch:</b> ${getBranchName()}</p>
      <div class="cards">
        <div class="card" style="background:#f0fdf4;border-color:#22c55e"><small>Total Credits (Receipts)</small><b>${fmt(totals.totalReceipts)}</b></div>
        <div class="card" style="background:#fef2f2;border-color:#ef4444"><small>Total Expenditures</small><b>${fmt(totals.totalExpenses)}</b></div>
        <div class="card" style="background:#faf5ff;border-color:#a855f7"><small>Loans Disbursed</small><b>${fmt(totals.totalLoansDisbursed)}</b></div>
        <div class="card" style="background:#eff6ff;border-color:#3b82f6"><small>Net Balance</small><b>${fmt(totals.balance)}</b></div>
      </div>
      <h3>Fund Receipts (Credits)</h3>
      ${filteredFunds.length ? `<table><thead><tr><th>Date</th><th>Fund Head</th><th>Description</th><th>Receipt No.</th><th>Amount</th></tr></thead><tbody>${fundRows}<tr class="total-row"><td colspan="4"><b>Total Credits</b></td><td style="text-align:right"><b>${fmt(totals.totalReceipts)}</b></td></tr></tbody></table>` : '<p style="color:#666;font-style:italic">No receipts in this period.</p>'}
      <h3>Expenditures (Debits)</h3>
      ${filteredExpenditures.length ? `<table><thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Voucher No.</th><th>Amount</th></tr></thead><tbody>${expRows}<tr class="total-row"><td colspan="4"><b>Total Expenditures</b></td><td style="text-align:right"><b>${fmt(totals.totalExpenses)}</b></td></tr></tbody></table>` : '<p style="color:#666;font-style:italic">No expenditures in this period.</p>'}
      <h3>Loan Disbursements (Debits)</h3>
      ${filteredLoans.length ? `<table><thead><tr><th>Date</th><th>Loan ID</th><th>Entrepreneur</th><th>Loan Type</th><th>Amount</th></tr></thead><tbody>${loanRows}<tr class="total-row"><td colspan="4"><b>Total Disbursed</b></td><td style="text-align:right"><b>${fmt(totals.totalLoansDisbursed)}</b></td></tr></tbody></table>` : '<p style="color:#666;font-style:italic">No loan disbursements in this period.</p>'}
    `;
    openPrintWindow(buildPrintHTML(`Cashbook — ${range.label}`, body, getBranchName()));
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.setTextColor(4, 120, 87); doc.text('WWAGS MIS Portal', 14, 20);
    doc.setFontSize(14); doc.setTextColor(0, 0, 0); doc.text('Cashbook Report', 14, 30);
    doc.setFontSize(10);
    doc.text(`Period: ${range.label}`, 14, 38);
    doc.text(`Branch: ${getBranchName()}`, 14, 44);
    doc.text('Fund Receipts', 14, 54);
    autoTable(doc, { startY: 58, head: [['Date', 'Fund Head', 'Amount', 'Receipt No.']], body: filteredFunds.map(f => [new Date(f.date).toLocaleDateString('en-IN'), f.fund_head, `Rs. ${(f.amount || 0).toFixed(2)}`, f.receipt_number || '-']), theme: 'grid', headStyles: { fillColor: [4, 120, 87] } });
    doc.text('Expenditures', 14, doc.lastAutoTable.finalY + 10);
    autoTable(doc, { startY: doc.lastAutoTable.finalY + 14, head: [['Date', 'Category', 'Amount', 'Voucher No.']], body: filteredExpenditures.map(e => [new Date(e.date).toLocaleDateString('en-IN'), e.category, `Rs. ${(e.amount || 0).toFixed(2)}`, e.voucher_number || '-']), theme: 'grid', headStyles: { fillColor: [194, 65, 12] } });
    doc.save(`Cashbook_${getBranchName().replace(/\s+/g, '_')}_${range.label.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    toast.success('PDF downloaded');
  };

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const data = [
      ['WWAGS MIS Portal - Cashbook'],
      [`Branch: ${getBranchName()}`],
      [`Period: ${range.label}`],
      [],
      ['FUND RECEIPTS'],
      ['Date', 'Fund Head', 'Amount', 'Receipt No.'],
      ...filteredFunds.map(f => [new Date(f.date).toLocaleDateString('en-IN'), f.fund_head, f.amount, f.receipt_number]),
      [],
      ['EXPENDITURES'],
      ['Date', 'Category', 'Amount', 'Voucher No.'],
      ...filteredExpenditures.map(e => [new Date(e.date).toLocaleDateString('en-IN'), e.category, e.amount, e.voucher_number]),
      [],
      ['LOAN DISBURSEMENTS'],
      ['Date', 'Loan ID', 'Entrepreneur', 'Loan Type', 'Amount'],
      ...filteredLoans.map(l => [l.disbursement_date ? new Date(l.disbursement_date).toLocaleDateString('en-IN') : '-', l.loan_id, l.entrepreneur_name, l.loan_type, l.loan_amount]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Cashbook');
    XLSX.writeFile(wb, `Cashbook_${getBranchName().replace(/\s+/g, '_')}_${range.label.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`);
    toast.success('Excel downloaded');
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="cashbook-report-page">
      <div className="flex items-start gap-3 mb-4">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700 mt-1"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Cashbook</h1>
          <p className="text-sm text-slate-600">Detailed record of all cash receipts and payments. Use date range to slice by day, month, quarter, half-year, FY or custom.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 mb-4 flex flex-wrap items-end gap-3">
        {isAdmin() && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Branch</span>
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-56 h-9" data-testid="cashbook-branch-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <DateRangeFilter value={range} onChange={setRange} />
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={handlePrint} data-testid="print-cashbook"><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button variant="outline" onClick={downloadPDF}><Download className="w-4 h-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={downloadExcel}><Download className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3"><p className="text-xs text-emerald-700 mb-1">Total Receipts</p><p className="text-xl font-bold text-emerald-900">{fmt(totals.totalReceipts)}</p></div>
        <div className="bg-orange-50 rounded-lg border border-orange-200 p-3"><p className="text-xs text-orange-700 mb-1">Expenditures</p><p className="text-xl font-bold text-orange-900">{fmt(totals.totalExpenses)}</p></div>
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-3"><p className="text-xs text-purple-700 mb-1">Loans Disbursed</p><p className="text-xl font-bold text-purple-900">{fmt(totals.totalLoansDisbursed)}</p></div>
        <div className="bg-sky-50 rounded-lg border border-sky-200 p-3"><p className="text-xs text-sky-700 mb-1">Bank → Cash</p><p className="text-xl font-bold text-sky-900">{fmt(totals.totalB2C)}</p></div>
        <div className="bg-rose-50 rounded-lg border border-rose-200 p-3"><p className="text-xs text-rose-700 mb-1">Cash → Bank</p><p className="text-xl font-bold text-rose-900">{fmt(totals.totalC2B)}</p></div>
        <div className={`rounded-lg border p-3 ${totals.balance >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}><p className={`text-xs mb-1 ${totals.balance >= 0 ? 'text-blue-700' : 'text-red-700'}`}>Net Balance</p><p className={`text-xl font-bold ${totals.balance >= 0 ? 'text-blue-900' : 'text-red-900'}`}>{fmt(totals.balance)}</p></div>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Fund Receipts (Credits) — {filteredFunds.length}</h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-emerald-700 text-white"><tr><th className="px-3 py-2 text-left font-semibold">Date</th><th className="px-3 py-2 text-left font-semibold">Fund Head</th><th className="px-3 py-2 text-left font-semibold">Description</th><th className="px-3 py-2 text-left font-semibold">Receipt No.</th><th className="px-3 py-2 text-right font-semibold">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredFunds.length === 0 ? <tr><td colSpan="5" className="px-3 py-4 text-center text-slate-500 italic">No receipts in selected period</td></tr> : filteredFunds.map(fund => (
                  <tr key={fund.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{new Date(fund.date).toLocaleDateString('en-IN')}</td>
                    <td className="px-3 py-2 text-slate-900 font-medium">{fund.fund_head}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{fund.description || '-'}</td>
                    <td className="px-3 py-2 text-slate-600">{fund.receipt_number}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmt(fund.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500"></span>Expenditures (Debits) — {filteredExpenditures.length}</h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-orange-600 text-white"><tr><th className="px-3 py-2 text-left font-semibold">Date</th><th className="px-3 py-2 text-left font-semibold">Category</th><th className="px-3 py-2 text-left font-semibold">Description</th><th className="px-3 py-2 text-left font-semibold">Voucher No.</th><th className="px-3 py-2 text-right font-semibold">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredExpenditures.length === 0 ? <tr><td colSpan="5" className="px-3 py-4 text-center text-slate-500 italic">No expenditures in selected period</td></tr> : filteredExpenditures.map(exp => (
                  <tr key={exp.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{new Date(exp.date).toLocaleDateString('en-IN')}</td>
                    <td className="px-3 py-2 text-slate-900 font-medium">{exp.category}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{exp.description || '-'}</td>
                    <td className="px-3 py-2 text-slate-600">{exp.voucher_number}</td>
                    <td className="px-3 py-2 text-right font-semibold text-orange-700">{fmt(exp.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-500"></span>Loan Disbursements (Debits) — {filteredLoans.length}</h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-purple-700 text-white"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Loan ID</th><th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Loan Type</th><th className="px-3 py-2 text-right">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLoans.length === 0 ? <tr><td colSpan="5" className="px-3 py-4 text-center text-slate-500 italic">No loans in selected period</td></tr> : filteredLoans.map(loan => (
                  <tr key={loan.loan_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{loan.disbursement_date ? new Date(loan.disbursement_date).toLocaleDateString('en-IN') : '-'}</td>
                    <td className="px-3 py-2 font-medium text-blue-700">{loan.loan_id}</td>
                    <td className="px-3 py-2 text-slate-900">{loan.entrepreneur_name}</td>
                    <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{loan.loan_type}</span></td>
                    <td className="px-3 py-2 text-right font-semibold text-purple-700">{fmt(loan.loan_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-sky-500"></span>Cash Transfers (Bank ↔ Cash) — {filteredTransfers.length}</h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-sky-700 text-white"><tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Direction</th>
                <th className="px-3 py-2 text-left">Bank</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransfers.length === 0 ? <tr><td colSpan="6" className="px-3 py-4 text-center text-slate-500 italic">No cash transfers in selected period</td></tr> : filteredTransfers.map(t => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{t.transfer_date ? new Date(t.transfer_date).toLocaleDateString('en-IN') : '-'}</td>
                    <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${t.direction === 'bank_to_cash' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{t.direction === 'bank_to_cash' ? 'Bank → Cash' : 'Cash → Bank'}</span></td>
                    <td className="px-3 py-2 text-slate-700">{t.bank_name || '-'}</td>
                    <td className="px-3 py-2 text-xs font-mono">{t.reference_no || '-'}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{t.description || '-'}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${t.direction === 'bank_to_cash' ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashbookReport;
